/** @jsx jsx */
import { css, jsx, keyframes, useTheme } from '@emotion/react';

import * as React from 'react';
import { connectCurrentRefinements } from 'react-instantsearch-core';
import type { CurrentRefinementsProvided } from 'react-instantsearch-core';

import classNames from 'classnames';
import type Maybe from 'graphql/tsutils/Maybe';
import { branch, compose, setDisplayName } from 'recompose';

import Imgix from 'js/components/Imgix';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Typography, Typography2 } from '@coursera/cds-core';

// save/unsave experiment
import type { withSaveDataForSearchCards } from 'bundles/browse/types/withSavedDataFromProducts';
import getBadgeConstants from 'bundles/common/constants/ProjectBadgeTitle';
import SFCDesktopSearchCardFragment from 'bundles/coursera-plus-common/components/search/SFCDesktopSearchCardFragment';
import { getAppliedFilters } from 'bundles/coursera-plus-common/components/search/utils/getAppliedFilters';
import ProjectBadge from 'bundles/enterprise-legacy-learner-home/components/ProjectBadge';
import withMiniModal from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type { Hit } from 'bundles/internal-search/types';
import TrackedButton from 'bundles/page/components/TrackedButton';
import type { TrackedButtonProps } from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import ProductInfo from 'bundles/search-common/components/ProductInfo';
import ProductTypePill from 'bundles/search-common/components/ProductTypePill';
import {
  COURSE,
  DEGREE,
  GUIDED_PROJECT,
  RHYME_PROJECT,
  SELF_PACED_PROJECT,
} from 'bundles/search-common/constants/entityTypes';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import { getPartners, getSearchCardImageUrl, isShortFormContent } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/search';

const MiniXDPButton = withMiniModal<TrackedButtonProps>()(TrackedButton);

export type Props = withSaveDataForSearchCards & {
  hit: Hit;
  isProgramHome?: boolean;
  isSEOEntityPage?: boolean;
  isBrowsePage?: boolean;
  hitPosition?: number;
  queryID?: string;
  listIndex?: number;
  forceOpenInNewTab?: boolean;
  removeLinks?: boolean;
  noResults?: boolean;
  hideProductTypePill?: boolean;
  categoryTags?: Array<string>;
  isSelectedForCredit?: boolean;
  isExclusive?: boolean;
  privateSession?: { startsAt: number; endsAt: number } | null;
  isOnSearchPage?: boolean;
  showCourseraLitePill?: boolean;
};

type DesktopSearchCardImageProps = {
  hit: {
    name: string;
    partners: string[];
    objectID?: Maybe<string>;
    imageUrl?: Maybe<string>;
    partnerLogos?: Maybe<Array<string>>;
  };
  hitPosition?: number;
};

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;
const styles = {
  desktopSearchCard: css`
    display: block;
    min-height: 150px;
    max-width: 1200px;
    width: 100%;
    border: 0;
    padding: 12px 0;
    cursor: pointer;

    &.remove-links {
      cursor: default;

      .card-content {
        cursor: default;
      }
    }

    &.parity-card {
      min-height: 100px;

      .image-wrapper {
        width: 100px;

        .product-photo {
          width: 100px;
          height: 100px;
        }
      }

      .card-content {
        min-height: 50px;

        .partner-name {
          padding: 3px 0;
          margin-left: 10px;
          margin-bottom: 0;
        }
      }
    }

    &.entity-page-card {
      .card-content {
        cursor: inherit;
      }
    }

    &.browse-result-card {
      .card-content {
        background-color: inherit !important;
        cursor: inherit;
      }
    }

    .image-wrapper {
      position: relative;
      height: 152px;
      width: 152px;
      margin-right: 20px;
      float: left;
      overflow: hidden;
      align-items: center;
      border: 1px solid var(--cds-color-grey-50);
      box-shadow: 0 0 9px -1px rgba(0 0 0 0.16);
      border-radius: 2px;

      .product-photo {
        width: 150px;
        height: 150px;
      }
    }

    .card-content {
      background-color: var(--cds-color-white-0);
      min-height: 100px;

      .partner-name {
        display: inline-block;
        margin: 4px 0;
      }

      .partner-logo-wrapper {
        padding-bottom: 6px;
        display: flex;
        align-items: center;
      }

      .partner-logo {
        border-radius: 3px;
        border: 1px solid var(--cds-color-grey-600);
        margin-right: 5px;
      }

      .product-type-row {
        & > div {
          &:not(:first-of-type) {
            margin-left: 8px;
          }
        }

        .product-difficulty {
          border-left: 1px solid var(--cds-color-grey-975);
          padding-left: 8px;
        }
      }

      .card-info {
        text-align: left;
        justify-content: space-between;
        padding-right: 6px;
        width: calc(100% - 172px);

        .rc-ProductInfo {
          padding: 0;
        }
      }
    }

    .card-description {
      white-space: pre-wrap;
    }

    .text-light {
      color: var(--cds-color-grey-600);
    }

    .card-title {
      display: inline-block;
      margin-bottom: 0;
    }

    .product-badge {
      background-color: var(--cds-color-grey-300);
      color: var(--cds-color-white-0);
      display: inline-block;
      padding: 2px 8px;
    }

    .product-pill {
      &.placeholder {
        width: 84px;
        height: 24px;
        border-radius: 3px;
      }
    }

    .placeholder {
      min-width: 84px;
      height: 24px;
      background-color: var(--cds-color-grey-200);
      margin-bottom: 8px;
    }

    .shimmer {
      animation: ${shimmer} 1.3s linear infinite forwards;
      background: linear-gradient(
        to right,
        var(--cds-color-grey-25) 8%,
        var(--cds-color-grey-50) 18%,
        var(--cds-color-grey-25) 33%
      );
      background-size: 1000px 104px;
      position: relative;
      overflow: hidden;
    }

    .badge-style {
      height: 19px;
      padding: 1px 7px;
      margin-left: 7px;
    }

    .rc-PrivateCourseLabel {
      margin-top: 2px;
      margin-bottom: 2px;
      font-size: 13px;
      color: var(--cds-color-grey-600);

      span {
        color: var(--cds-color-grey-600);
        font-weight: 600;
        margin: 0 12px;
      }
    }

    .result-title-link {
      color: var(--cds-color-grey-975) !important;

      &:hover,
      &:visited {
        color: var(--cds-color-grey-975) !important;
      }

      &:hover {
        text-decoration: none !important;

        .card-title {
          text-decoration: underline !important;
        }
      }
    }

    .program-home-desktop-search-card {
      border: none;
      background: none;
      outline: none;
      width: 100%;
    }
  `,
};

export const getPrivateSessionInfo = (session?: { startsAt: number; endsAt: number }) => {
  if (session?.startsAt && session?.endsAt) {
    const startsAt = formatDateTimeDisplay(session?.startsAt, 'MMM D');
    const endsAt = formatDateTimeDisplay(session?.endsAt, 'MMM D, YYYY');
    return <div className="private-session-dates">{`${startsAt} - ${endsAt}`}</div>;
  } else {
    return null;
  }
};

export const EnterpriseCreditBadge = ({ className }: { className: string }) => {
  return (
    <span className={`forCreditBadge enterpriseBadge ${className}`} aria-label={_t('Marked as for credit')}>
      {_t('Credit')}
    </span>
  );
};

export const EnterpriseExclusiveBadge = ({ className }: { className: string }) => {
  return (
    <span
      className={`exclusiveBadge enterpriseBadge ${className}`}
      aria-label={_t('Marked as exclusive for your organization')}
    >
      {_t('Exclusive')}
    </span>
  );
};

const DesktopSearchCardImage = ({ hit, hitPosition }: DesktopSearchCardImageProps) => {
  let altTagContent = hit.name;
  let ImgixComponent: $TSFixMe = Imgix;

  // If card is greater than 5th element in list, use LazyImgix for perf boost.
  if (hitPosition && hitPosition > 4) {
    ImgixComponent = LazyImgix;
  }

  if ((hit.partners || []).length) {
    altTagContent += ` by ${hit.partners.join(', ')}`;
  }

  return (
    <div className="image-wrapper">
      <ImgixComponent
        src={getSearchCardImageUrl(hit)}
        alt={altTagContent}
        maxWidth={150}
        maxHeight={150}
        width={150}
        height={150}
        imgParams={{ fit: 'fill', auto: 'format,compress', bg: 'FFF', quality: '25' }}
        className="product-photo"
      />
    </div>
  );
};

const PrivateCourseLabel = ({ partner }: { partner?: string }) => (
  <div className="rc-PrivateCourseLabel">
    <FormattedHTMLMessage
      tagName="div"
      hasPartner={!!partner}
      partner={partner}
      message={_t(
        'Course created {hasPartner, select, true {by {partner}} false {}} exclusively for your learning program'
      )}
    />
  </div>
);

export const DesktopSearchCard = ({
  hit,
  hitPosition,
  queryID,
  isProgramHome,
  isSEOEntityPage,
  isBrowsePage,
  listIndex,
  forceOpenInNewTab,
  removeLinks = false,
  noResults = false,
  hideProductTypePill = false,
  showCourseraLitePill = false,
  isSaved,
  loadingSavedProducts,
  isExclusive,
  isSelectedForCredit,
  privateSession,
  items,
}: Props & CurrentRefinementsProvided) => {
  const theme = useTheme();
  if (!isSearchTheme(theme)) {
    throw new Error(
      'DesktopSearchCard can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }
  if (!hit) return null;

  const hitID = hit.objectID && hit.objectID.split('~')[1];
  const { enrollments, isPrivate, productDifficultyLevel, entityType, avgProductRating, numProductRatings, partners } =
    hit;

  const onClickToNewWindow = (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const breadcrumbLessonLinkElement = e?.target as HTMLElement;
    if (!breadcrumbLessonLinkElement.className.includes('product-name-link')) {
      window.open(hit.objectUrl, '_blank');
    }
  };

  const cardWrapperClasses = classNames('anchor-wrapper', {
    'entity-page-card': isSEOEntityPage,
    'browse-result-card': isBrowsePage,
    'remove-links': removeLinks,
    'program-home-desktop-search-card': isProgramHome,
  });

  const shouldShowNewBadge = [GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT].includes(entityType);

  const hasNoResults = noResults ? 'no_results_search_card' : 'search_card';
  const trackingName = isBrowsePage ? 'browse_result_card' : hasNoResults;
  const badgeConstants = getBadgeConstants();

  const filtersApplied = getAppliedFilters(items);
  const sharedProps = {
    data: { objectID: hit.objectID, hitPosition, queryID, listIndex, noResults, filtersApplied }, // note that query will be included in `context`
    withVisibilityTracking: true,
    requireFullyVisible: true,
    className: cardWrapperClasses,
    'data-e2e': 'DesktopSearchCard',
    trackingName,
  };

  const children = (
    <Grid container className="card-content">
      <Grid item className="card-image">
        <DesktopSearchCardImage hit={hit} hitPosition={hitPosition} />
      </Grid>
      <Grid item className="card-info">
        <Typography component="h2" variant="h2semibold" className="card-title">
          {hit.name}
        </Typography>
        <Grid container>
          <Grid item>
            <Typography2 component="span" variant="bodySecondary" color="supportText" className="partner-name">
              {getPartners(hitID, !!hit.cobrandingEnabled, partners)}
            </Typography2>
          </Grid>
          {shouldShowNewBadge && (
            <Grid item>
              <ProjectBadge text={badgeConstants.new} />
            </Grid>
          )}
        </Grid>
        <Grid container className="product-type-container">
          <Grid item className="product-type-row">
            {entityType && !hideProductTypePill && <ProductTypePill productTypeName={entityType} />}
            {isExclusive && <EnterpriseExclusiveBadge className="m-l-1" />}
            {isSelectedForCredit && <EnterpriseCreditBadge className="m-l-1" />}
            {privateSession && getPrivateSessionInfo(privateSession)}
            {isPrivate && <PrivateCourseLabel partner={partners ? partners[0] : undefined} />}
          </Grid>
        </Grid>
        <ProductInfo
          tagline={
            entityType === DEGREE ||
            // show tagline if none of the other data is available
            (!enrollments && !avgProductRating && !productDifficultyLevel)
              ? hit.tagline
              : null
          }
          enrollments={enrollments}
          avgProductRating={avgProductRating}
          numProductRatings={numProductRatings}
          productDifficultyLevel={productDifficultyLevel}
          isPartOfCourseraPlus={hit.isPartOfCourseraPlus}
          // save/unsave experiment
          id={hit.objectID}
          entityType={entityType}
          isSaved={isSaved}
          showCourseraLitePill={showCourseraLitePill}
          loadingSavedProducts={loadingSavedProducts}
        />
      </Grid>
    </Grid>
  );

  if (isProgramHome) {
    return (
      <div className="rc-DesktopSearchCard cds" css={styles.desktopSearchCard}>
        {isShortFormContent(entityType) ? (
          <TrackedDiv {...sharedProps} onClick={onClickToNewWindow} aria-label={hit.name}>
            <SFCDesktopSearchCardFragment hit={hit} hitPosition={hitPosition} />
          </TrackedDiv>
        ) : (
          <MiniXDPButton
            {...sharedProps}
            miniModalProps={{
              isCourse: [COURSE, GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT].includes(entityType),
              id: hitID,
            }}
          >
            {children}
          </MiniXDPButton>
        )}
      </div>
    );
    // If `removeLinks` is true, there should *no* interactivity at all
    // This is used in the Coursera Plus landing page to prevent users from losing focus and going to search
  } else if (removeLinks) {
    return (
      <div className="rc-DesktopSearchCard remove-links cds" css={styles.desktopSearchCard}>
        <TrackedDiv {...sharedProps} aria-label={hit.name}>
          {children}
        </TrackedDiv>
      </div>
    );
  } else {
    return (
      <div className="rc-DesktopSearchCard cds" css={styles.desktopSearchCard}>
        <TrackedLink2
          {...sharedProps}
          type="a"
          trackingName={trackingName}
          data={sharedProps.data}
          href={hit.objectUrl}
          className="result-title-link"
          target={forceOpenInNewTab ? '_blank' : undefined}
          rel={forceOpenInNewTab ? 'noopener noreferrer' : undefined}
        >
          {children}
        </TrackedLink2>
      </div>
    );
  }
};

export default compose<Props & CurrentRefinementsProvided, Props>(
  setDisplayName('DesktopSearchCard'),
  branch<Props>(({ isOnSearchPage }) => !!isOnSearchPage, connectCurrentRefinements)
)(DesktopSearchCard);
